/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { invalidateDashboardQueries } from "@/dashboard/dashboardQueryConfig";
import {
  PROGRAM_QUERY_GC_TIME,
  PROGRAM_QUERY_STALE_TIMES,
  programQueryKeys,
  removeProgramProjectFromProgramsInfoCache,
  upsertProgramProjectInProgramsInfoCache,
} from "@/dashboard/programQueryConfig";
import { useModal } from "@/dashboard/store/store";
import {
  useFetchDataV2,
  writeFetchDataV2BrowserCache,
} from "@/hooks/useFetchDataV2";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import ProgramSummary from "./ProgramSummary";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ProjectList from "../project/ProjectList";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import AddProject from "@/pages/dashboard/AddProject";
import { useDatabase } from "@/programs/hooks/useDatabase";
import { useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader,
  X,
} from "lucide-react";
import MapClusterProgramsAll from "./MapClusterProgramsAll";

const FormSchema = z.object({
  programPhoto: z.string().optional(),
  programName: z
    .string()
    .min(1, {
      message: "Le nom du programme est obligatoire.",
    })
    .max(120, { message: "120 caractères maximum" }),
  programDescription: z.string().optional(),
});

interface MyData {
  name: string;
  description: string;
}

type ViewMode = "program" | "project";

type FundSearcherFilter = "all" | "me" | "both";
type FinancingFilter = "all" | "full" | "partial" | "none";
type StatusFilter = "all" | 0 | 1 | 2 | 3 | 4 | 5 | 6;

const STATUS_LABELS: Record<number, string> = {
  0: "Non commencé",
  1: "En cours",
  2: "En attente de validation",
  3: "Validé",
  4: "Rejeté",
  5: "Cloturé",
  6: "En attente de votre validation",
};

type ProgramExportRow = {
  programName: string;
  programDescription?: string;
  programCoverUri?: string | null;
  projectCount: number;
  validatedCount: number;
  rejectedCount: number;
  inProgressCount: number;
  notStartedCount: number;
  programUrl: string;
};

const isProjectReadyForRouteCache = (project: any) =>
  Boolean(
    project?.id &&
      !project?.__optimistic &&
      project?.program?.id &&
      project?.type?.id &&
      "description" in project &&
      "manager" in project,
  );

const sanitizeAsciiFilename = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x20-\x7E]/g, "")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();

const getFileNameFromDisposition = (
  disposition: string | null,
  fallback: string,
) => {
  const asciiFallback = sanitizeAsciiFilename(fallback);
  if (!disposition) return asciiFallback;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = disposition.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] ?? asciiFallback;
};

const downloadGlobalProgramsExport = async (
  format: "xlsx" | "docx",
  rows: ProgramExportRow[],
) => {
  const token =
    localStorage.getItem("jwtToken") ?? sessionStorage.getItem("jwtToken");
  const response = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/program/export/global/${format}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ rows }),
    },
  );

  if (!response.ok) {
    throw new Error("Erreur lors de l'export des programmes");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getFileNameFromDisposition(
    response.headers.get("content-disposition"),
    `Rapport-programmes.${format}`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const GlobalProgramExportDialog = ({
  isOpen,
  onOpenChange,
  onExportExcel,
  onExportWord,
  isExporting,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExportExcel: () => Promise<void>;
  onExportWord: () => Promise<void>;
  isExporting: boolean;
}) => {
  const [selectedFormat, setSelectedFormat] = useState<"xlsx" | "docx">("xlsx");

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat("xlsx");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full min-w-0 max-w-[720px] rounded-[28px] border-[#d6e2d2] bg-[linear-gradient(180deg,#fbfdf9_0%,#f2f7ef_100%)] p-0 shadow-2xl sm:min-w-0">
        <div className="overflow-hidden rounded-[28px]">
          <div className="bg-[linear-gradient(135deg,#1f4b1d_0%,#5f8e53_60%,#94bf85_100%)] px-8 py-7 text-white">
            <div className="flex items-start justify-between gap-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                  <Download className="h-3.5 w-3.5" />
                  Rapport global des programmes
                </div>
                <DialogTitle className="mt-4 text-[28px] font-semibold leading-tight text-white">
                  Exporter la liste générale des programmes
                </DialogTitle>
                <DialogDescription className="mt-2 max-w-[560px] text-sm leading-6 text-white/85">
                  Générez un rapport consolidé avec le nom du programme, ses
                  volumes de projets, les statuts clés et l’URL d’accès au
                  programme.
                </DialogDescription>
              </div>
              <div className="hidden rounded-[24px] border border-white/15 bg-white/10 p-4 md:block">
                <FileSpreadsheet className="h-10 w-10 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-5 px-8 py-7">
            <div className="grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => setSelectedFormat("xlsx")}
                className={`relative overflow-hidden rounded-[24px] border p-5 text-left transition-all ${
                  selectedFormat === "xlsx"
                    ? "border-[#6f9a5c] bg-white shadow-[0_18px_50px_rgba(75,107,60,0.15)] ring-2 ring-[#dfead9]"
                    : "border-[#d9e4d2] bg-white/80 hover:border-[#b7c9ae] hover:bg-white"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#2e5f22_0%,#8fb37f_100%)]" />
                <div className="rounded-[18px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                  <IconExcel className="h-8 w-8" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#24451f]">
                  Excel
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#5d6e58]">
                  Export structuré pour analyser rapidement les volumes de
                  projets, les statuts et les liens des programmes.
                </p>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat("docx")}
                className={`relative overflow-hidden rounded-[24px] border p-5 text-left transition-all ${
                  selectedFormat === "docx"
                    ? "border-[#8ba5d1] bg-white shadow-[0_18px_50px_rgba(86,112,163,0.12)] ring-2 ring-[#e4ebf8]"
                    : "border-[#d9e4d2] bg-white/80 hover:border-[#c8d5e8] hover:bg-white"
                }`}
              >
                <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#45638f_0%,#9ab4de_100%)]" />
                <div className="rounded-[18px] bg-[#eef4ff] p-3 text-[#45638f]">
                  <IconWord className="h-8 w-8" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#314664]">
                  Word
                </h3>
                <p className="mt-2 text-sm leading-6 text-[#66758a]">
                  Rapport simplifié présentant les informations clés sous forme de texte, tableaux et synthèses.
                </p>
              </button>
            </div>

            <div className="rounded-[22px] border border-[#d7e3d2] bg-white/90 p-5">
              {selectedFormat === "xlsx" ? (
                <div className="flex items-start gap-4">
                  <div className="rounded-[16px] bg-[#edf6e9] p-3 text-[#2a5b1e]">
                    <FileSpreadsheet className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#294b24]">
                      Contenu de l'export Excel
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#5f6d5c]">
                      Une vue tabulaire des programmes avec le nombre total de
                      projets, les projets validés, rejetés, en cours, non
                      commencés et l’URL du programme.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  <div className="rounded-[16px] bg-[#eef4ff] p-3 text-[#45638f]">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#314664]">
                      Contenu de l'export Word
                    </p>
                    <p className="mt-1 text-sm leading-6 text-[#66758a]">
                      Rapport simple avec les informations principales des programmes, les statuts des projets et l’URL.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-3 border-t border-[#dde7d8] bg-white/80 px-8 py-5 sm:justify-between sm:gap-3">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-[#cfdcc9] bg-white px-5 text-[#496045] hover:bg-[#f4f8f2]"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              className="rounded-xl bg-[linear-gradient(135deg,#21511c_0%,#5f8f51_100%)] px-6 text-white shadow-lg hover:opacity-95"
              onClick={() => {
                if (selectedFormat === "xlsx") {
                  void onExportExcel();
                } else {
                  void onExportWord();
                }
              }}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  Export en cours
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Exporter
                </>
              )}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ProgramList = () => {
  const queryClient = useQueryClient();
  const programsInfoEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/info/programs`;
  const programsOwnersEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/programs`;
  const countriesEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/countries`;
  const programsBrowserCacheOptions = {
    browserCache: true,
    browserCacheKey: "programsInfo",
    browserCacheNamespace: "programs",
    browserCacheTtlMs: PROGRAM_QUERY_GC_TIME,
  } as const;
  const cachedProgramsInfo = queryClient.getQueryData<any[]>([
    programQueryKeys.programsInfo,
    programsInfoEndpoint,
  ]);

  const programsInfo = useFetchDataV2<any[]>(programsInfoEndpoint, {
    ...programsBrowserCacheOptions,
    cacheId: programQueryKeys.programsInfo,
    staleTime: PROGRAM_QUERY_STALE_TIMES.programsInfo,
    gcTime: PROGRAM_QUERY_GC_TIME,
  });
  const programsOwners = useFetchDataV2<any[]>(
    programsOwnersEndpoint,
    programQueryKeys.programsOwners,
    PROGRAM_QUERY_STALE_TIMES.programsInfo,
    PROGRAM_QUERY_GC_TIME,
  );

  // ✅ Countries API (countries, not country)
  const countriesQuery = useFetchDataV2<any[]>(
    countriesEndpoint,
    programQueryKeys.countries,
    PROGRAM_QUERY_STALE_TIMES.countries,
    PROGRAM_QUERY_GC_TIME,
  );

  const [programs, setPrograms] = useState<any[]>(
    Array.isArray(cachedProgramsInfo) ? cachedProgramsInfo : [],
  );
  const pendingProgramNavigationIdRef = useRef<number | null>(null);
  const { t } = useTranslation("dashboard", { keyPrefix: "programs" });
  const { handleCompressImage } = useImageCompressor();
  const { postData, loading: loadingSave, response } = usePostData<MyData>();
  const hidePostActions = useHidePostActions();
  const postMultipart = usePostMultipart();
  const { updateProgramInDB } = useDatabase();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();


  const initialView = useMemo<ViewMode>(() => {
    return searchParams.get("view") === "project" ? "project" : "program";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [viewMode, setViewMode] = useState<ViewMode>(initialView);

  const [uploadPhotoLoading, setUploadPhotoLoading] = useState(false);
  const { setOpenModal, modals, programId: modalProgramId } = useModal();
  const { user } = useAuth();
  const [isGlobalExportDialogOpen, setIsGlobalExportDialogOpen] =
    useState(false);
  const [isGlobalExporting, setIsGlobalExporting] = useState(false);

  // =========================
  // ✅ FILTERS
  // =========================
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [launchFrom, setLaunchFrom] = useState<string>("");
  const [launchTo, setLaunchTo] = useState<string>("");

  const [countryId, setCountryId] = useState<number | null>(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  const [fundSearcher, setFundSearcher] = useState<FundSearcherFilter>("all"); // moi-même = 1 ; sinon moi-même & Verdeo
  const [financing, setFinancing] = useState<FinancingFilter>("all");
  const [status, setStatus] = useState<StatusFilter>("all");

  const countriesList = useMemo(() => {
    return Array.isArray(countriesQuery?.data) ? countriesQuery.data : [];
  }, [countriesQuery?.data]);

  const selectedCountry = useMemo(() => {
    if (!countryId) return null;
    return countriesList.find((c: any) => Number(c.id) === Number(countryId));
  }, [countriesList, countryId]);

  const visibleCountries = useMemo(() => {
    const q = (countrySearch || "").trim().toLowerCase();
    if (!q) return countriesList;
    return countriesList.filter((c: any) =>
      String(c?.name || "")
        .toLowerCase()
        .includes(q),
    );
  }, [countriesList, countrySearch]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      launchFrom ||
      launchTo ||
      countryId ||
      fundSearcher !== "all" ||
      financing !== "all" ||
      status !== "all",
    );
  }, [launchFrom, launchTo, countryId, fundSearcher, financing, status]);

  const computeFundingState = (p: any): FinancingFilter => {
    const df = p?.desiredFunding;
    const of = p?.obtainedFunding;

    const desiredArr = Array.isArray(df) ? df : [];
    const obtainedArr = Array.isArray(of) ? of : [];

    const isFull =
      df === null ||
      df === undefined ||
      desiredArr.length === 0 ||
      Number(p?.soldStatus) === 2;

    if (isFull) return "full";

    const isPartial =
      desiredArr.length > 0 &&
      obtainedArr.length > 0 &&
      Number(p?.soldStatus) !== 2;

    if (isPartial) return "partial";

    const isNone = desiredArr.length > 0 && Number(p?.soldStatus) !== 2;

    return isNone ? "none" : "all";
  };

  const matchProject = (p: any) => {
    // 1) launchDate interval
    if (launchFrom || launchTo) {
      const ld = p?.launchDate ? new Date(p.launchDate) : null;
      if (!ld || Number.isNaN(ld.getTime())) return false;

      if (launchFrom) {
        const fromDate = new Date(`${launchFrom}T00:00:00`);
        if (ld < fromDate) return false;
      }

      if (launchTo) {
        const toDate = new Date(`${launchTo}T23:59:59`);
        if (ld > toDate) return false;
      }
    }

    // 2) country
    if (countryId) {
      if (Number(p?.country?.id) !== Number(countryId)) return false;
    }

    // 3) fund searcher
    if (fundSearcher === "me") {
      if (Number(p?.searchFinancingInternal) !== 1) return false;
    }
    if (fundSearcher === "both") {
      // "Moi-même et Verdeo" = tout sauf 1 (null/0/2...)
      if (Number(p?.searchFinancingInternal) === 1) return false;
    }

    // 4) financing
    if (financing !== "all") {
      const state = computeFundingState(p);
      if (state !== financing) return false;
    }

    // 5) status
    if (status !== "all") {
      if (Number(p?.status) !== Number(status)) return false;
    }

    return true;
  };

  const filteredPrograms = useMemo(() => {
    const arr = Array.isArray(programs) ? programs : [];

    const enriched = arr.map((prog: any) => {
      const allProjects = Array.isArray(prog?.projects) ? prog.projects : [];
      const filteredProjects = hasActiveFilters
        ? allProjects.filter(matchProject)
        : allProjects;

      return {
        ...prog,
        _allProjects: allProjects,
        _filteredProjects: filteredProjects,
      };
    });

    return enriched;
  }, [
    programs,
    hasActiveFilters,
    launchFrom,
    launchTo,
    countryId,
    fundSearcher,
    financing,
    status,
  ]);

  const totalProjectsCount = useMemo(() => {
    const arr = Array.isArray(programs) ? programs : [];
    return arr.reduce((acc: number, prog: any) => {
      const projs = Array.isArray(prog?.projects) ? prog.projects : [];
      return acc + projs.length;
    }, 0);
  }, [programs]);

  const filteredProjectsCount = useMemo(() => {
    return filteredPrograms.reduce((acc: number, prog: any) => {
      const projs = Array.isArray(prog?._filteredProjects)
        ? prog._filteredProjects
        : [];
      return acc + projs.length;
    }, 0);
  }, [filteredPrograms]);

  const exportRows = useMemo<ProgramExportRow[]>(() => {
    const currentOrigin = window.location.origin;

    return filteredPrograms.map((program: any) => {
      const projects = hasActiveFilters
        ? Array.isArray(program?._filteredProjects)
          ? program._filteredProjects
          : []
        : Array.isArray(program?._allProjects)
          ? program._allProjects
          : Array.isArray(program?.projects)
            ? program.projects
            : [];

      const countByStatus = (statuses: number[]) =>
        projects.filter((project: any) =>
          statuses.includes(Number(project?.status)),
        ).length;

      return {
        programName: program?.name || "-",
        programDescription: program?.description || "",
        programCoverUri:
          typeof program?.cover?.uri === "string" && program.cover.uri.trim() !== ""
            ? program.cover.uri
            : typeof program?.defaultImage?.uri === "string" &&
                program.defaultImage.uri.trim() !== ""
              ? program.defaultImage.uri
              : null,
        projectCount: projects.length,
        validatedCount: countByStatus([3]),
        rejectedCount: countByStatus([4]),
        inProgressCount: countByStatus([1, 2, 6, 7]),
        notStartedCount: countByStatus([0]),
        programUrl: `${currentOrigin}/dashboard/program/${program?.id}`,
      };
    });
  }, [filteredPrograms, hasActiveFilters]);

  const filterSummary = useMemo(() => {
    const parts: string[] = [];

    if (launchFrom || launchTo)
      parts.push(`Lancement: ${launchFrom || "…"} → ${launchTo || "…"} `);
    if (countryId) parts.push(`Pays: ${selectedCountry?.name || "…"}`);
    if (fundSearcher !== "all") {
      parts.push(
        `Chercheur: ${
          fundSearcher === "me" ? "Moi-même" : "Moi-même et Verdeo"
        }`,
      );
    }
    if (financing !== "all") {
      parts.push(
        `Financement: ${
          financing === "full"
            ? "Totalement financé"
            : financing === "partial"
              ? "Partiellement financé"
              : "Non financé"
        }`,
      );
    }
    if (status !== "all")
      parts.push(`Statut: ${STATUS_LABELS[Number(status)] || status}`);

    return parts.length ? parts.join(" • ") : "Aucun filtre";
  }, [
    launchFrom,
    launchTo,
    countryId,
    selectedCountry,
    fundSearcher,
    financing,
    status,
  ]);

  const handleResetFilters = () => {
    setLaunchFrom("");
    setLaunchTo("");
    setCountryId(null);
    setCountrySearch("");
    setFundSearcher("all");
    setFinancing("all");
    setStatus("all");
  };

  // =========================
  // ✅ Existing ProgramList logic
  // =========================
  useEffect(() => {
    if (searchParams.get("openmodal") == "true") {
      setTimeout(() => {
        setOpenModal({ name: "program", value: true });
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [nameChar, setNameChar] = useState(0);

  const handleViewChange = (next: ViewMode) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("view", next);
    setSearchParams(sp, { replace: true });
    setViewMode(next);
  };

  const handlePhotoProgram = async (value) => {
    if (value) {
      setUploadPhotoLoading(true);

      const compressedBlob = await handleCompressImage(value[0], 500, 300, 0.9);

      const data = new FormData();
      data.append("type", 8);
      const fileDefaultName = value[0].name;

      if (compressedBlob) {
        for (let i = 0; i < value.length; i++) {
          const renamedFile = new File([compressedBlob], fileDefaultName, {
            type: compressedBlob.type,
          });
          data.append("files[]", renamedFile);
        }
      } else {
        for (let i = 0; i < value.length; i++) {
          data.append("files[]", value[0]);
        }
      }

      const newMedias = await postMultipart.postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });

      if (
        !Array.isArray(newMedias) &&
        newMedias.success !== undefined &&
        newMedias.success !== null &&
        !newMedias.success
      ) {
        toast({ title: newMedias.message, duration: 1000 });
        setUploadPhotoLoading(false);
        return;
      }

      setManagerLogo(newMedias[0]);
      setUploadPhotoLoading(false);
      return;
    }
  };

  const [managerLogo, setManagerLogo] = useState(response?.data?.name || null);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      programPhoto: "",
      programName: "",
      programDescription: "",
    },
  });

  const upsertProgramInCache = (
    nextProgram: any,
    previousProgramId?: number,
  ) => {
    const updater = (current: any) => {
      if (!Array.isArray(current)) return current;

      const existingIndex = current.findIndex((program: any) => {
        const currentId = Number(program?.id || 0);
        return (
          currentId === Number(nextProgram?.id || 0) ||
          (previousProgramId && currentId === Number(previousProgramId))
        );
      });

      if (existingIndex >= 0) {
        return current.map((program: any, index: number) =>
          index === existingIndex
            ? {
                ...program,
                ...nextProgram,
              }
            : program,
        );
      }

      return [...current, nextProgram];
    };

    setPrograms((prevPrograms) => {
      const nextPrograms = updater(prevPrograms);
      writeFetchDataV2BrowserCache(programsInfoEndpoint, nextPrograms, {
        browserCache: true,
        browserCacheKey: "programsInfo",
        browserCacheNamespace: "programs",
        browserCacheTtlMs: 1000 * 60 * 60,
      });
      return nextPrograms;
    });
    queryClient.setQueriesData(
      {
        exact: false,
        queryKey: [programQueryKeys.programsInfo],
      },
      (current: any) => {
        const nextPrograms = updater(current);
        writeFetchDataV2BrowserCache(programsInfoEndpoint, nextPrograms, {
          browserCache: true,
          browserCacheKey: "programsInfo",
          browserCacheNamespace: "programs",
          browserCacheTtlMs: 1000 * 60 * 60,
        });
        return nextPrograms;
      },
    );
    queryClient.setQueryData(
      [programQueryKeys.programsInfo, programsInfoEndpoint],
      (current: any) => updater(Array.isArray(current) ? current : programs),
    );
    const nextPrograms =
      queryClient.getQueryData<any[]>([
        programQueryKeys.programsInfo,
        programsInfoEndpoint,
      ]) ?? updater(programs);
    if (Array.isArray(nextPrograms)) {
      writeFetchDataV2BrowserCache(
        programsInfoEndpoint,
        nextPrograms,
        programsBrowserCacheOptions,
      );
    }
  };

  const removeProgramFromCache = (programId: number) => {
    const updater = (current: any) => {
      if (!Array.isArray(current)) return current;
      return current.filter(
        (program: any) => Number(program?.id || 0) !== Number(programId),
      );
    };

    setPrograms((prevPrograms) => {
      const nextPrograms = updater(prevPrograms);
      writeFetchDataV2BrowserCache(programsInfoEndpoint, nextPrograms, {
        browserCache: true,
        browserCacheKey: "programsInfo",
        browserCacheNamespace: "programs",
        browserCacheTtlMs: 1000 * 60 * 60,
      });
      return nextPrograms;
    });
    queryClient.setQueriesData(
      {
        exact: false,
        queryKey: [programQueryKeys.programsInfo],
      },
      (current: any) => {
        const nextPrograms = updater(current);
        writeFetchDataV2BrowserCache(programsInfoEndpoint, nextPrograms, {
          browserCache: true,
          browserCacheKey: "programsInfo",
          browserCacheNamespace: "programs",
          browserCacheTtlMs: 1000 * 60 * 60,
        });
        return nextPrograms;
      },
    );
    queryClient.removeQueries({
      exact: false,
      queryKey: programQueryKeys.programInfo(programId),
    });
    const nextPrograms =
      queryClient.getQueryData<any[]>([
        programQueryKeys.programsInfo,
        programsInfoEndpoint,
      ]) ?? updater(programs);
    if (Array.isArray(nextPrograms)) {
      writeFetchDataV2BrowserCache(
        programsInfoEndpoint,
        nextPrograms,
        programsBrowserCacheOptions,
      );
    }
  };

  const writeProgramsInfoCache = (nextPrograms: any[]) => {
    writeFetchDataV2BrowserCache(
      programsInfoEndpoint,
      nextPrograms,
      programsBrowserCacheOptions,
    );
  };

  const upsertProjectInProgramCache = (
    programId: number,
    project: any,
    previousProjectId?: number,
  ) => {
    const updater = (current: any) => {
      if (!Array.isArray(current)) return current;

      const normalizedProgramId = Number(programId || 0);
      const normalizedProjectId = Number(project?.id || 0);
      const normalizedPreviousProjectId = Number(previousProjectId || 0);

      return current.map((program: any) => {
        if (Number(program?.id || 0) !== normalizedProgramId) {
          return program;
        }

        const projects = Array.isArray(program?.projects)
          ? program.projects
          : [];
        const existingIndex = projects.findIndex((item: any) => {
          const itemId = Number(item?.id || 0);
          return (
            itemId === normalizedProjectId ||
            (normalizedPreviousProjectId &&
              itemId === normalizedPreviousProjectId)
          );
        });
        const nextProjects =
          existingIndex >= 0
            ? projects.map((item: any, index: number) =>
                index === existingIndex ? { ...item, ...project } : item,
              )
            : [...projects, project];

        return {
          ...program,
          projects: nextProjects,
        };
      });
    };

    setPrograms((prevPrograms) => updater(prevPrograms));
    upsertProgramProjectInProgramsInfoCache(
      queryClient,
      programId,
      project,
      previousProjectId,
    );

    const nextPrograms =
      queryClient.getQueryData<any[]>([
        programQueryKeys.programsInfo,
        programsInfoEndpoint,
      ]) ?? updater(programs);
    if (Array.isArray(nextPrograms)) {
      writeProgramsInfoCache(nextPrograms);
    }
  };

  const removeProjectFromProgramCache = (
    programId: number,
    projectId: number,
  ) => {
    const updater = (current: any) => {
      if (!Array.isArray(current)) return current;

      return current.map((program: any) => {
        if (Number(program?.id || 0) !== Number(programId || 0)) {
          return program;
        }

        const projects = Array.isArray(program?.projects)
          ? program.projects
          : [];

        return {
          ...program,
          projects: projects.filter(
            (project: any) =>
              Number(project?.id || 0) !== Number(projectId || 0),
          ),
        };
      });
    };

    setPrograms((prevPrograms) => updater(prevPrograms));
    removeProgramProjectFromProgramsInfoCache(queryClient, programId, projectId);
    queryClient.removeQueries({
      exact: false,
      queryKey: [`project-${projectId}`],
    });

    const nextPrograms =
      queryClient.getQueryData<any[]>([
        programQueryKeys.programsInfo,
        programsInfoEndpoint,
      ]) ?? updater(programs);
    if (Array.isArray(nextPrograms)) {
      writeProgramsInfoCache(nextPrograms);
    }
  };

  const handleDeleteProjectOptimistic = async (
    programId: number,
    projectId: number,
    deleteRequest: () => Promise<any>,
  ) => {
    const previousPrograms = programs;
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });
    const previousProgramInfoQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: programQueryKeys.programInfo(programId),
    });

    removeProjectFromProgramCache(programId, projectId);

    const res = await deleteRequest();
    const hasFailed =
      res === null ||
      (typeof res === "object" && "success" in res && !res.success);

    if (hasFailed) {
      setPrograms(previousPrograms);
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      writeProgramsInfoCache(previousPrograms);
      return res;
    }

    void queryClient.invalidateQueries({
      exact: false,
      queryKey: ["programsInfoTree"],
    });
    void queryClient.invalidateQueries({
      exact: false,
      queryKey: ["AapProject"],
    });

    return typeof res === "object" && res !== null ? res : { success: true };
  };

  const extractDuplicatedProject = (res: any) => {
    if (!res || typeof res !== "object") return null;
    if (res.id) return res;

    const candidates = [
      res.project,
      res.data,
      res.duplicatedProject,
      res.projectDuplicated,
      res.newProject,
      res.result,
    ];

    for (const candidate of candidates) {
      if (candidate?.id) return candidate;
      if (Array.isArray(candidate)) {
        const project = candidate.find((item: any) => item?.id);
        if (project) return project;
      }
    }

    return Array.isArray(res.projects)
      ? res.projects.find((candidate: any) => candidate?.id) ?? null
      : null;
  };

  const handleDuplicateProjectOptimistic = async (
    programId: number,
    project: any,
    duplicateRequest: () => Promise<any>,
  ) => {
    const previousPrograms = programs;
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });
    const previousProgramInfoQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: programQueryKeys.programInfo(programId),
    });
    const tempProjectId = -Date.now();
    const currentProgram = programs.find(
      (program: any) => Number(program?.id ?? 0) === Number(programId),
    );
    const optimisticProject = {
      ...project,
      id: tempProjectId,
      name: `${project?.name ?? project?.title ?? t("Projet")} - copie`,
      status: project?.status ?? 1,
      soldStatus: project?.soldStatus ?? 0,
      program: project?.program ?? {
        id: programId,
        name: currentProgram?.name,
      },
      __optimistic: true,
    };

    upsertProjectInProgramCache(programId, optimisticProject);

    let res = null;
    try {
      res = await duplicateRequest();
    } catch (error) {
      setPrograms(previousPrograms);
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      writeProgramsInfoCache(previousPrograms);
      return { success: false, error };
    }
    const hasFailed =
      res === null ||
      (typeof res === "object" && "success" in res && !res.success);

    if (hasFailed) {
      setPrograms(previousPrograms);
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      writeProgramsInfoCache(previousPrograms);
      return res;
    }

    const duplicatedProject = extractDuplicatedProject(res);
    if (duplicatedProject?.id) {
      upsertProjectInProgramCache(
        programId,
        {
          ...duplicatedProject,
          program: duplicatedProject.program ?? {
            id: programId,
            name: currentProgram?.name,
          },
          __optimistic: false,
        },
        tempProjectId,
      );
    }

    void queryClient.invalidateQueries({
      exact: false,
      queryKey: ["programsInfoTree"],
    });
    invalidateDashboardQueries(queryClient, "projectLifecycle");

    return typeof res === "object" && res !== null ? res : { success: true };
  };

  const handleDeleteProgramOptimistic = async (
    programId: number,
    deleteRequest: () => Promise<any>,
  ) => {
    const previousPrograms = programs;
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });

    removeProgramFromCache(programId);

    const res = await deleteRequest();
    const hasFailed =
      res === null ||
      (typeof res === "object" && "success" in res && !res.success);

    if (hasFailed) {
      setPrograms(previousPrograms);
      writeFetchDataV2BrowserCache(programsInfoEndpoint, previousPrograms, {
        browserCache: true,
        browserCacheKey: "programsInfo",
        browserCacheNamespace: "programs",
        browserCacheTtlMs: 1000 * 60 * 60,
      });
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      return res;
    }

    window.setTimeout(() => {
      void queryClient.invalidateQueries({
        exact: false,
        queryKey: [programQueryKeys.programsInfo],
      });
      void queryClient.invalidateQueries({
        exact: false,
        queryKey: ["programsInfoTree"],
      });
    }, 1000);

    return res ?? true;
  };

  const handleOpenProgram = (program: any) => {
    const programId = Number(program?.id || 0);
    if (!program?.__optimistic || programId > 0) {
      return true;
    }

    pendingProgramNavigationIdRef.current = programId;
    toast({
      title: t("Création du programme en cours..."),
      duration: 1000,
    });
    return false;
  };

  async function onSubmit(formData: z.infer<typeof FormSchema>) {
    const data = {
      name: formData.programName,
      description: formData.programDescription || "",
      ...(managerLogo?.id && { cover: managerLogo.id }),
    };
    const tempProgramId = -Date.now();
    const optimisticProgram = {
      id: tempProgramId,
      name: data.name,
      description: data.description,
      cover: managerLogo ?? null,
      createdAt: new Date().toISOString(),
      projects: [],
      status: 1,
      carrier: user,
      access: true,
      __optimistic: true,
    };

    upsertProgramInCache(optimisticProgram);
    form.reset();
    setManagerLogo(null);
    setNameChar(0);
    setOpenModal({ name: "program", value: false });

    const result = await postData({
      url: `${import.meta.env.VITE_API_ENDPOINT}/program/save`,
      data,
    });

    if (!result?.id) {
      removeProgramFromCache(tempProgramId);
      setOpenModal({ name: "program", value: true });
      toast({
        title: t("Une erreur est survenue lors de la création du programme."),
        duration: 2500,
      });
      return;
    }

    const newProgram = {
      id: result.id as number,
      name: result.name as string,
      description: result.description as string,
      cover: result.cover as string,
      createdAt: result.createdAt as Date,
      projects: Array.isArray(result.projects) ? result.projects : [],
      status: result.status as number,
      carrier: result.carrier ?? user,
      access: result.access ?? true,
      __optimistic: false,
      __recentlyCreated: true,
    };

    upsertProgramInCache(newProgram, tempProgramId);
    const programInfoEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/info/programs?program=${newProgram.id}`;
    queryClient.setQueryData(programQueryKeys.programInfo(newProgram.id), [
      newProgram,
    ]);
    writeFetchDataV2BrowserCache(programInfoEndpoint, [newProgram], {
      browserCache: true,
      browserCacheKey: `programInfo-${newProgram.id}`,
      browserCacheNamespace: "programs",
      browserCacheTtlMs: PROGRAM_QUERY_GC_TIME,
    });
    form.reset();
    setManagerLogo(null);
    setNameChar(0);
    if (pendingProgramNavigationIdRef.current === tempProgramId) {
      pendingProgramNavigationIdRef.current = null;
      navigate(`/dashboard/program/${newProgram.id}`);
    }
    window.setTimeout(() => {
      void queryClient.invalidateQueries({
        exact: false,
        queryKey: [programQueryKeys.programsInfo],
      });
      void queryClient.invalidateQueries({
        exact: false,
        queryKey: programQueryKeys.programInfo(newProgram.id),
      });
    }, 1500);

    toast({
      title: t("Programme créé avec succès"),
      duration: 2000,
    });

    if (pendingProgramNavigationIdRef.current === tempProgramId) {
      pendingProgramNavigationIdRef.current = null;
      navigate(`/dashboard/program/${newProgram.id}`);
    }
  }

  const handleSubmitProject = async (
    e: FormEvent<HTMLFormElement>,
    projectsTemp: any,
    projectsTempBdd: any,
    programId: number,
  ) => {
    e.preventDefault();
    window.scrollTo(0, 0);

    const projectsArray: any[] = [];
    projectsTemp.map((project: any) => {
      if (project.type)
        projectsArray.push({ name: project.name, type: project.type });
    });

    const projectsArrayBdd: any[] = [];
    projectsTempBdd?.map((project: any) => {
      if (project.type)
        projectsArrayBdd.push({ name: project.name, type: project.type });
    });

    const selectedProject = projectsArrayBdd[projectsArrayBdd.length - 1];
    const currentProgram = programs.find(
      (program: any) => Number(program?.id ?? 0) === Number(programId),
    );
    const previousPrograms = programs;
    const previousProgramQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: [programQueryKeys.programsInfo],
    });
    const previousProgramInfoQueries = queryClient.getQueriesData({
      exact: false,
      queryKey: programQueryKeys.programInfo(programId),
    });
    const existingProjectIds = new Set(
      (Array.isArray(currentProgram?.projects) ? currentProgram.projects : [])
        .map((project: any) => Number(project?.id ?? 0))
        .filter((projectId: number) => Number.isFinite(projectId) && projectId > 0),
    );
    const tempProjectId = -Date.now();
    if (selectedProject) {
      upsertProjectInProgramCache(programId, {
        id: tempProjectId,
        name: selectedProject.name,
        type: selectedProject.type,
        status: 1,
        soldStatus: 0,
        __optimistic: true,
      });
      setOpenModal({ name: "project", value: false });
    }

    let programSaved = null;
    try {
      programSaved = await updateProgramInDB(
        { projects: [...projectsArrayBdd] },
        programId,
      );
    } catch (error) {
      if (selectedProject) {
        setPrograms(previousPrograms);
        previousProgramQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        previousProgramInfoQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
        writeProgramsInfoCache(previousPrograms);
        setOpenModal({ name: "project", value: true, programId });
        toast({
          className: "text-red-500",
          title: t("Une erreur est survenue lors de la création du projet."),
          duration: 2500,
        });
      }
      return;
    }

    const savedProject =
      programSaved?.projects?.find(
        (project: any) => !existingProjectIds.has(Number(project?.id ?? 0)),
      ) ??
      programSaved?.projects?.[programSaved.projects.length - 1] ??
      null;
    if (selectedProject && !savedProject) {
      setPrograms(previousPrograms);
      previousProgramQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      previousProgramInfoQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      writeProgramsInfoCache(previousPrograms);
      setOpenModal({ name: "project", value: true, programId });
      toast({
        className: "text-red-500",
        title: t("Une erreur est survenue lors de la création du projet."),
        duration: 2500,
      });
      return;
    }

    toast({ title: t("Vos informations sont bien prises en compte.") });
    if (programSaved?.projects && selectedProject) {
      if (savedProject) {
        const projectForCache = {
          ...savedProject,
          program: savedProject.program ?? {
            id: programSaved.id ?? programId,
            name: programSaved.name ?? currentProgram?.name,
          },
        };
        upsertProjectInProgramCache(
          programId,
          { ...projectForCache, __optimistic: false },
          tempProjectId,
        );
        if (isProjectReadyForRouteCache(projectForCache)) {
          [
            { tab: "descriptif", excludes: "indicator,reporting" },
            { tab: "synthetic", excludes: "indicator,reporting" },
            { tab: "suivi-perf", excludes: "reporting" },
            { tab: "reporting", excludes: "indicator" },
          ].forEach((item) => {
            const endpoint = `${import.meta.env.VITE_API_ENDPOINT}/project/${savedProject.id}?excludes=${item.excludes}`;
            queryClient.setQueryData(
              [`project-${savedProject.id}-${item.tab}-v2`, endpoint],
              projectForCache,
            );
          });
        }
      }
    }
    queryClient.invalidateQueries({ queryKey: ["programsInfoTree"] });
    invalidateDashboardQueries(queryClient, "projectLifecycle");

    if (savedProject?.id) {
      navigate(`/dashboard/project/${savedProject.id}`);
    }
  };

  useEffect(() => {
    if (!Array.isArray(programsInfo?.data)) return;

    setPrograms((prevPrograms) => {
      const optimisticPrograms = Array.isArray(prevPrograms)
        ? prevPrograms.filter((program: any) => program?.__optimistic)
        : [];
      const recentlyCreatedPrograms = Array.isArray(prevPrograms)
        ? prevPrograms.filter((program: any) => program?.__recentlyCreated)
        : [];
      const serverProgramIds = new Set(
        programsInfo.data.map((program: any) => Number(program?.id || 0)),
      );
      const pendingLocalPrograms = [
        ...optimisticPrograms,
        ...recentlyCreatedPrograms,
      ].filter(
        (program: any) => !serverProgramIds.has(Number(program?.id || 0)),
      );

      return [...programsInfo.data, ...pendingLocalPrograms];
    });
  }, [programsInfo?.data]);

  if (programsInfo?.error)
    return (
      <p className="text-red-500">
        {t("Une erreur est survenue : ")}
        {programsInfo?.error}
      </p>
    );

  const canAddProgram = user?.roles?.includes("ROLE_CARRIER");

  const handleExportPrograms = async (format: "xlsx" | "docx") => {
    if (0 === exportRows.length) {
      toast({
        title: "Aucun programme à exporter",
        duration: 2000,
      });
      return;
    }

    setIsGlobalExporting(true);
    try {
      await downloadGlobalProgramsExport(format, exportRows);
      setIsGlobalExportDialogOpen(false);
    } catch (_error) {
      toast({
        title:
          format === "xlsx"
            ? "Impossible d'exporter les programmes en Excel"
            : "Impossible d'exporter les programmes en Word",
        duration: 2500,
      });
    } finally {
      setIsGlobalExporting(false);
    }
  };

  return (
    <>
      <GlobalProgramExportDialog
        isOpen={isGlobalExportDialogOpen}
        onOpenChange={setIsGlobalExportDialogOpen}
        onExportExcel={() => handleExportPrograms("xlsx")}
        onExportWord={() => handleExportPrograms("docx")}
        isExporting={isGlobalExporting}
      />

      <div className="mb-5 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-medium">{t("Liste de mes programmes")}</h1>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl border-[#cfdcc9] bg-white px-4 text-[#496045] hover:bg-[#f4f8f2]"
            onClick={() => setIsGlobalExportDialogOpen(true)}
          >
            <Download className="mr-2 h-4 w-4" />
            Exporter
          </Button>

          {/* switch vue programme / vue projet */}
          <div className="inline-flex items-center rounded-xl bg-muted p-1 ring-1 ring-border">
            <Button
              type="button"
              size="sm"
              variant={viewMode === "program" ? "secondary" : "ghost"}
              className="rounded-lg"
              onClick={() => handleViewChange("program")}
            >
              Vue programme
            </Button>
            <Button
              type="button"
              size="sm"
              variant={viewMode === "project" ? "secondary" : "ghost"}
              className="rounded-lg"
              onClick={() => handleViewChange("project")}
            >
              Vue projet
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap">
        <div className="w-3/5">
          {/* ✅ FILTERS (collapsible) — above the list */}
          <div className="mx-4 mb-4 rounded-2xl border bg-background p-3">
            <button
              type="button"
              className="flex w-full items-start justify-between gap-3"
              onClick={() => setFiltersOpen((v) => !v)}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex size-9 items-center justify-center rounded-xl bg-muted">
                  <Filter className="size-5 text-muted-foreground" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-semibold text-foreground">
                    Filtres projets
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {filterSummary}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {hasActiveFilters
                      ? `${filteredProjectsCount} projet${
                          filteredProjectsCount > 1 ? "s" : ""
                        } affiché${filteredProjectsCount > 1 ? "s" : ""} / ${
                          totalProjectsCount || 0
                        }`
                      : `${totalProjectsCount || 0} projet${
                          (totalProjectsCount || 0) > 1 ? "s" : ""
                        }`}
                  </div>
                </div>
              </div>

              <div className="shrink-0 inline-flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetFilters();
                  }}
                  title="Réinitialiser"
                >
                  <X className="size-4" />
                </Button>
                {filtersOpen ? (
                  <ChevronUp className="size-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="size-5 text-muted-foreground" />
                )}
              </div>
            </button>

            {filtersOpen && (
              <div className="mt-4 grid grid-cols-1 gap-3">
                {/* Date launch */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Date de lancement (début)
                    </div>
                    <Input
                      type="date"
                      value={launchFrom}
                      onChange={(e) => setLaunchFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Date de lancement (fin)
                    </div>
                    <Input
                      type="date"
                      value={launchTo}
                      onChange={(e) => setLaunchTo(e.target.value)}
                    />
                  </div>
                </div>

                {/* Country searchable */}
                <div>
                  <div className="mb-1 text-xs font-semibold text-foreground">
                    Pays
                  </div>
                  <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between"
                      >
                        <span className="truncate">
                          {selectedCountry?.name || "Tous les pays"}
                        </span>
                        <ChevronDown className="ml-2 size-4 opacity-60" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-2" align="start">
                      <div className="mb-2">
                        <Input
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          placeholder="Rechercher un pays…"
                        />
                      </div>

                      <div className="max-h-60 overflow-auto rounded-md border">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onClick={() => {
                            setCountryId(null);
                            setCountryOpen(false);
                          }}
                        >
                          Tous les pays
                        </button>

                        {visibleCountries.map((c: any) => (
                          <button
                            key={c.id}
                            type="button"
                            className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setCountryId(Number(c.id));
                              setCountryOpen(false);
                            }}
                          >
                            <span className="truncate">{c.name}</span>
                            {Number(countryId) === Number(c.id) ? (
                              <Check className="size-4 opacity-70" />
                            ) : null}
                          </button>
                        ))}

                        {!visibleCountries.length && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            Aucun résultat
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Fund searcher */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Chercheur de fond
                    </div>
                    <select
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                      value={fundSearcher}
                      onChange={(e) =>
                        setFundSearcher(e.target.value as FundSearcherFilter)
                      }
                    >
                      <option value="all">Tous</option>
                      <option value="me">Moi-même</option>
                      <option value="both">Moi-même et Verdeo</option>
                    </select>
                  </div>

                  <div>
                    <div className="mb-1 text-xs font-semibold text-foreground">
                      Financement
                    </div>
                    <select
                      className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                      value={financing}
                      onChange={(e) =>
                        setFinancing(e.target.value as FinancingFilter)
                      }
                    >
                      <option value="all">Tous</option>
                      <option value="full">Totalement financé</option>
                      <option value="partial">Partiellement financé</option>
                      <option value="none">Non financé</option>
                    </select>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <div className="mb-1 text-xs font-semibold text-foreground">
                    Statut
                  </div>
                  <select
                    className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                    value={String(status)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStatus(
                        v === "all" ? "all" : (Number(v) as StatusFilter),
                      );
                    }}
                  >
                    <option value="all">Tous</option>
                    <option value="0">Non commencé</option>
                    <option value="1">En cours</option>
                    <option value="2">En attente de validation</option>
                    <option value="3">Validé</option>
                    <option value="4">Rejeté</option>
                    <option value="5">Cloturé</option>
                    <option value="6">En attente de votre validation</option>
                  </select>
                </div>

                <div className="flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResetFilters}
                  >
                    Réinitialiser
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* =========================
              VUE PROGRAMME
             ========================= */}
          {viewMode === "program" && (
            <div className="mx-4 flex flex-wrap items-start gap-y-4">
              {programsInfo?.loading && programs.length === 0 && (
                <CardSkeleton />
              )}

              {(programs.length > 0 || !programsInfo?.loading) &&
                filteredPrograms?.map((program: any, key: number) => (
                  (() => {
                    const ownerProgram = programsOwners?.data?.find(
                      (item: any) => Number(item?.id) === Number(program?.id),
                    );

                    return (
                      <ProgramSummary
                        className="w-full px-4 md:w-1/2 lg:w-1/3 "
                        onOpenProgram={handleOpenProgram}
                        onDeleteProgram={handleDeleteProgramOptimistic}
                        key={program?.id ?? key}
                        program={{
                          ...program,
                          carrier: program?.carrier ?? ownerProgram?.carrier,
                          projects:
                            program?._filteredProjects ?? program?.projects ?? [],
                        }}
                      />
                    );
                  })()
                ))}

              {/* ADD PROGRAM (inchangé) */}
              <div className="modal-program mb-4 w-full min-w-52 px-4 md:w-1/2 lg:w-1/3 ">
                <div className=" h-[175px] w-full " id="addProgram">
                  <Dialog
                    open={modals["program"]}
                    onOpenChange={(value: boolean) =>
                      setOpenModal({ name: "program", value: value })
                    }
                  >
                    <DialogTrigger asChild>
                      {!hidePostActions && canAddProgram && (
                          <div className="flex size-full flex-col items-start justify-center">
                            <Button
                              disabled={programsInfo?.loading && programs.length === 0}
                              variant="outline"
                              className=" flex size-full items-center justify-center rounded-[15px] border"
                              style={{ background: "#EFEFEF" }}
                            >
                              <div className="flex w-max  items-center">
                                <svg
                                  width="48"
                                  height="36"
                                  viewBox="0 0 48 36"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M4.61257 36C3.34411 36 2.25863 35.5597 1.35609 34.6793C0.452031 33.7973 0 32.7375 0 31.5V4.5C0 3.2625 0.452031 2.20351 1.35609 1.323C2.25863 0.441 3.34411 0 4.61257 0H16.5476C17.1626 0 17.7492 0.1125 18.3073 0.3375C18.8639 0.5625 19.3536 0.881249 19.7765 1.29375L23.0628 4.5H41.5131C42.7815 4.5 43.8679 4.941 44.772 5.823C45.6744 6.70351 46.1257 7.7625 46.1257 9H21.1602L16.5476 4.5H4.61257V31.5L9.16747 16.7062C9.47498 15.7312 10.0423 14.9527 10.8695 14.3707C11.6952 13.7902 12.6077 13.5 13.6071 13.5H43.3581C44.9341 13.5 46.1742 14.109 47.0782 15.327C47.9806 16.5465 48.2205 17.8687 47.7976 19.2938L43.6463 32.7938C43.3389 33.7687 42.7723 34.5472 41.9467 35.1293C41.1195 35.7098 40.2063 36 39.2068 36H4.61257ZM9.45576 31.5H39.2068L43.3581 18H13.6071L9.45576 31.5Z"
                                    fill="#D4D4D4"
                                  />
                                  <rect
                                    x="25"
                                    y="19"
                                    width="3"
                                    height="11"
                                    rx="1"
                                    fill="#D9D9D9"
                                  />
                                  <rect
                                    x="32"
                                    y="23"
                                    width="3"
                                    height="11"
                                    rx="1"
                                    transform="rotate(90 32 23)"
                                    fill="#D9D9D9"
                                  />
                                </svg>
                              </div>
                            </Button>
                            <div className=" mt-4 flex cursor-pointer items-center gap-2 font-semibold text-foreground opacity-70">
                              {t("Ajouter")}
                            </div>
                          </div>
                        )}
                    </DialogTrigger>

                    <DialogContent className="sm:max-w-[600px]">
                      <Form {...form}>
                        <form
                          onSubmit={form.handleSubmit(onSubmit)}
                          className="space-y-6"
                        >
                          <DialogHeader>
                            <DialogTitle className="text-2xl">
                              {t("Ajout de programme")}
                            </DialogTitle>
                            <DialogDescription>
                              {t("Remplissez")}
                            </DialogDescription>
                          </DialogHeader>

                          <div className="flex flex-wrap">
                            <div className="basis-1/3 pr-4">
                              <FormLabel className="mb-3 block">
                                {t("Image du programme")}
                              </FormLabel>
                              <FileUploader
                                value={[]}
                                onValueChange={handlePhotoProgram}
                                dropzoneOptions={{ multiple: false }}
                                className={`relative h-full rounded-lg bg-background`}
                              >
                                <FileInput className="border-indigo-foreground size-full border-2 border-dashed">
                                  {!uploadPhotoLoading &&
                                    managerLogo === null && (
                                      <div className="flex size-full flex-col items-center justify-center pb-4 pt-3">
                                        <svg
                                          className="size-8 text-gray-500 dark:text-gray-400"
                                          aria-hidden="true"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 20 16"
                                        >
                                          <path
                                            stroke="currentColor"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                          />
                                        </svg>
                                        {t("Photo")}
                                      </div>
                                    )}

                                  {!uploadPhotoLoading &&
                                    managerLogo !== null && (
                                      <div className="flex size-full flex-col items-center justify-center">
                                        <img
                                          src={
                                            import.meta.env.VITE_MEDIA_BASE +
                                            managerLogo.uri
                                          }
                                          alt=""
                                          className="size-full object-cover"
                                        />
                                      </div>
                                    )}

                                  {uploadPhotoLoading && (
                                    <div
                                      className={`w-35 relative mr-2 flex h-24 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-input bg-gray-200`}
                                    >
                                      <div className="customfileuploaderloader">
                                        {Array.from({ length: 16 }).map(
                                          (_, i) => (
                                            <div
                                              className="block"
                                              key={i}
                                            ></div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </FileInput>
                              </FileUploader>
                            </div>

                            <div className="basis-2/3 pl-4">
                              <FormField
                                control={form.control}
                                name="programName"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      {t("Nom du programme")}
                                      <span className="text-destructive">
                                        *
                                      </span>
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        onKeyUp={(e) =>
                                          setNameChar(
                                            e.currentTarget.value.length,
                                          )
                                        }
                                        placeholder={t("Nom du programme")}
                                        {...field}
                                      />
                                    </FormControl>
                                    <div className="flex w-full">
                                      <div className="w-3/6">
                                        <FormMessage />
                                      </div>
                                      <FormDescription
                                        className={
                                          "w-3/6 text-right" +
                                          (nameChar > 120
                                            ? " text-red-500"
                                            : "")
                                        }
                                      >
                                        {nameChar} / 120&nbsp;{t("caractères")}
                                      </FormDescription>
                                    </div>
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="programDescription"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      {t("Description du programme")}
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder={t(
                                          "Description du programme",
                                        )}
                                        {...field}
                                        className="w-full"
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          </div>

                          <DialogFooter>
                            <Button type="submit" disabled={loadingSave}>
                              {loadingSave ? "..." : t("Sauvegarder")}
                            </Button>
                          </DialogFooter>
                        </form>
                      </Form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Empty state in program view when filters hide everything */}
              {!programsInfo?.loading &&
                hasActiveFilters &&
                filteredPrograms.length === 0 && (
                  <div className="w-full px-4">
                    <div className="rounded-2xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                      Aucun programme ne correspond aux filtres.
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* =========================
              VUE PROJET (accordéon)
             ========================= */}
          {viewMode === "project" && (
            <div className="mx-4">
              {programsInfo?.loading && programs.length === 0 && (
                <CardSkeleton />
              )}

              {(programs.length > 0 || !programsInfo?.loading) && (
                <>
                  {filteredPrograms.length === 0 ? (
                    <div className="rounded-2xl border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                      Aucun programme ne correspond aux filtres.
                    </div>
                  ) : (
                    <Accordion
                      type="multiple"
                      className="space-y-3"
                      defaultValue={
                        filteredPrograms?.length
                          ? [`program-${filteredPrograms[0]?.id}`]
                          : []
                      }
                    >
                      {filteredPrograms?.map((program: any) => {
                        const allProjects = Array.isArray(program?._allProjects)
                          ? program._allProjects
                          : Array.isArray(program?.projects)
                            ? program.projects
                            : [];

                        const projects = Array.isArray(
                          program?._filteredProjects,
                        )
                          ? program._filteredProjects
                          : allProjects;

                        const allCount = allProjects.length;
                        const shownCount = projects.length;

                        return (
                          <AccordionItem
                            key={program.id}
                            value={`program-${program.id}`}
                            className="rounded-2xl border bg-white px-2"
                          >
                            <AccordionTrigger className="w-full px-3 py-3 hover:no-underline">
                              <div className="flex w-full items-center justify-between gap-4">
                                <div className="min-w-0 text-left">
                                  <div className="truncate text-sm font-semibold text-slate-900">
                                    {program.name}
                                  </div>
                                  {program.description ? (
                                    <div className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                                      {program.description}
                                    </div>
                                  ) : null}
                                </div>

                                <div className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                                  {hasActiveFilters
                                    ? `${shownCount} / ${allCount} projet${
                                        allCount > 1 ? "s" : ""
                                      }`
                                    : `${allCount} projet${
                                        allCount > 1 ? "s" : ""
                                      }`}
                                </div>
                              </div>
                            </AccordionTrigger>

                            <AccordionContent className="px-3 pb-4 pt-2">
                              <div className="rounded-2xl border bg-slate-50 p-3">
                                {shownCount === 0 && (
                                  <div className="mb-3 rounded-xl border bg-white px-3 py-3 text-sm text-muted-foreground">
                                    {hasActiveFilters
                                      ? "Aucun projet ne correspond aux filtres dans ce programme."
                                      : 'Il n\'y a pas encore de projets dans ce programme. Veuillez en ajouter un en cliquant sur le bouton "Ajouter".'}
                                  </div>
                                )}
                                <ProjectList
                                  projects={projects}
                                  programId={program.id}
                                  programsParam={
                                    Array.isArray(programsOwners?.data)
                                      ? programsOwners.data
                                      : [program]
                                  }
                                  onDeleteProject={handleDeleteProjectOptimistic}
                                  onDuplicateProject={handleDuplicateProjectOptimistic}
                                />
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* MAP (inchangé) */}
        <div className="w-2/5 pl-6">
          <div className="sticky top-[92px] overflow-hidden rounded-lg">
            <MapClusterProgramsAll dataRead="All" />
          </div>
        </div>
      </div>

      {modals["project"] && (
        <AddProject
          onHandleSubmit={(e, projectsTemp, projectsTempBdd) => {
            handleSubmitProject(
              e,
              projectsTemp,
              projectsTempBdd,
              modalProgramId as number,
            );
          }}
        />
      )}
    </>
  );
};

export default ProgramList;

export const CardSkeleton = () => {
  return (
    <div className="flex flex-wrap items-start gap-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton className="h-[150px] w-[200px] rounded-[20px]" key={i} />
      ))}
    </div>
  );
};

export const MapSkeleton = () => {
  return (
    <div>
      <Skeleton className="h-[500px] w-[500px] rounded-lg bg-[#ecedee]" />
    </div>
  );
};
